import { useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";
import { FaBolt, FaUserSecret, FaFire, FaHistory } from "react-icons/fa";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
`;

/* ───────────────────────── Shell ───────────────────────── */

const Page = styled.div`
  background: #fff;
  min-height: 100vh;
  font-family: "Mendl", sans-serif;
  color: #0a0010;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 5vw, 3.5rem);
`;

/* ───────────────────────── Hero ───────────────────────── */

const Hero = styled.section`
  padding: clamp(6rem, 12vw, 8.5rem) 0 clamp(3rem, 6vw, 4rem);
  text-align: center;
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.6rem;

  &::before,
  &::after {
    content: "";
    width: 32px;
    height: 1px;
    background: #a855f7;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.6rem, 6vw, 4.6rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.01em;
  margin: 0 auto 1.2rem;
  max-width: 720px;

  em {
    font-style: italic;
    font-weight: 400;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const HeroSub = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1.05rem;
  font-weight: 300;
  line-height: 1.8;
  color: rgba(10, 0, 16, 0.6);
  max-width: 520px;
  margin: 0 auto;
`;

const TrialBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 1.4rem auto 0;
  padding: 0.5rem 1.1rem;
  border-radius: 100px;
  border: 1px solid rgba(168, 85, 247, 0.25);
  background: rgba(168, 85, 247, 0.06);
  font-family: "DM Sans", sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7c3aed;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #a855f7;
  }
`;

const TrialBadgeWrap = styled.div`
  display: flex;
  justify-content: center;
`;

/* ───────────────────────── Billing toggle ───────────────────────── */

const ToggleWrap = styled.div`
  display: flex;
  justify-content: center;
  margin: clamp(2.2rem, 4vw, 3rem) 0 clamp(2.5rem, 5vw, 3.5rem);
`;

const ToggleTrack = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  max-width: 320px;
  background: rgba(168, 85, 247, 0.06);
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 100px;
  padding: 4px;
`;

const ToggleThumb = styled.div`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: 4px;
  width: calc(50% - 4px);
  border-radius: 100px;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  transform: ${({ $yearly }) =>
    $yearly ? "translateX(100%)" : "translateX(0)"};
`;

const ToggleOption = styled.button`
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0.65rem 0.9rem;
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 100px;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(10, 0, 16, 0.55)")};
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  white-space: nowrap;

  @media (max-width: 420px) {
    font-size: 0.76rem;
    padding: 0.6rem 0.5rem;
    gap: 0.3rem;
  }
`;

const SaveTag = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 0.15rem 0.5rem;
  border-radius: 100px;
  white-space: nowrap;
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.22)" : "rgba(168, 85, 247, 0.14)"};
  color: ${({ $active }) => ($active ? "#fff" : "#7c3aed")};

  @media (max-width: 420px) {
    font-size: 0.58rem;
    padding: 0.1rem 0.38rem;
  }
`;

/* ───────────────────────── Price card ───────────────────────── */

const CardWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 460px;
  border-radius: 24px;
  padding: clamp(2.2rem, 4vw, 3rem);
  background: linear-gradient(
    160deg,
    rgba(232, 121, 249, 0.06),
    rgba(124, 58, 237, 0.06)
  );
  border: 1px solid rgba(168, 85, 247, 0.2);
  box-shadow: 0 20px 60px rgba(124, 58, 237, 0.08);
  text-align: center;
`;

const PlanName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const PlanTagline = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 300;
  color: rgba(10, 0, 16, 0.55);
  margin-bottom: 1.8rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.35rem;
  margin-bottom: 0.4rem;
`;

const PriceAmount = styled.div`
  font-size: clamp(3rem, 6vw, 3.8rem);
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const PricePeriod = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.95rem;
  font-weight: 400;
  color: rgba(10, 0, 16, 0.5);
`;

const PriceNote = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 400;
  color: rgba(10, 0, 16, 0.5);
  margin-bottom: 2rem;
  min-height: 1.2em;
`;

const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.95rem 1.5rem;
  border-radius: 100px;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  color: #fff;
  font-family: "DM Sans", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  box-shadow: 0 10px 30px rgba(124, 58, 237, 0.25);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 14px 36px rgba(124, 58, 237, 0.32);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const FinePrint = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 300;
  color: rgba(10, 0, 16, 0.42);
  margin-top: 1rem;
  line-height: 1.6;

  a {
    color: #7c3aed;
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

/* ───────────────────────── Features grid ───────────────────────── */

const FeaturesSection = styled.section`
  padding: clamp(4.5rem, 8vw, 6.5rem) 0;
`;

const SectionEyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 600;
  text-align: center;
  margin: 0 auto 3rem;
  max-width: 640px;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  max-width: 880px;
  margin: 0 auto;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  display: flex;
  gap: 1.1rem;
  padding: 1.6rem;
  border-radius: 16px;
  border: 1px solid rgba(168, 85, 247, 0.12);
  background: rgba(168, 85, 247, 0.02);
`;

const FeatureIcon = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #a855f7, #7c3aed);
  color: #fff;
  font-size: 1.1rem;
`;

const FeatureName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
`;

const FeatureDesc = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.87rem;
  font-weight: 300;
  line-height: 1.65;
  color: rgba(10, 0, 16, 0.6);
`;

/* ───────────────────────── Back home ───────────────────────── */

const BackHome = styled(Link)`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  color: #7c3aed;
  text-decoration: none;
  display: flex;
  justify-content: center;
  margin-bottom: 4rem;

  &:hover {
    text-decoration: underline;
  }
`;

/* ───────────────────────── Data ───────────────────────── */

const FEATURES = [
  {
    icon: <FaBolt />,
    name: "Priority Feed Placement",
    desc: "Your profile surfaces higher and more often in the feed, so you get seen first.",
  },
  {
    icon: <FaUserSecret />,
    name: "Stealth Anonymous Mode",
    desc: "Browse and interact without revealing your identity until you choose to.",
  },
  {
    icon: <FaFire />,
    name: "Unlimited Matches & Flares",
    desc: "No daily caps — match and send flares as much as you want.",
  },
  {
    icon: <FaHistory />,
    name: "Feed History Access",
    desc: "Go back and revisit profiles that have already scrolled past you.",
  },
];

const MONTHLY_PRICE = 9.99;
const YEARLY_PRICE = 99.99;
const YEARLY_MONTHLY_EQUIVALENT = (YEARLY_PRICE / 12).toFixed(2);
const ANNUAL_DISCOUNT_PERCENT = 16.59;

export default function PricingPage() {
  const [yearly, setYearly] = useState(false);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Membership</Eyebrow>
          <HeroTitle>
            Your ticket <em>in.</em>
          </HeroTitle>
          <HeroSub>
            NudR runs on membership — priority placement, stealth browsing, and
            unlimited matches are built in from day one. This is what it costs
            to join.
          </HeroSub>

          <TrialBadgeWrap>
            <TrialBadge>7-Day Free Trial</TrialBadge>
          </TrialBadgeWrap>

          <ToggleWrap>
            <ToggleTrack>
              <ToggleThumb $yearly={yearly} />
              <ToggleOption
                type="button"
                $active={!yearly}
                onClick={() => setYearly(false)}
              >
                Monthly
              </ToggleOption>
              <ToggleOption
                type="button"
                $active={yearly}
                onClick={() => setYearly(true)}
              >
                Yearly
                <SaveTag $active={yearly}>
                  Save {ANNUAL_DISCOUNT_PERCENT}%
                </SaveTag>
              </ToggleOption>
            </ToggleTrack>
          </ToggleWrap>

          <CardWrap>
            <Card>
              <PlanName>NudR Membership</PlanName>
              <PlanTagline>Required to join and use NudR</PlanTagline>

              <PriceRow>
                <PriceAmount>
                  ${yearly ? YEARLY_MONTHLY_EQUIVALENT : MONTHLY_PRICE}
                </PriceAmount>
                <PricePeriod>/month</PricePeriod>
              </PriceRow>
              <PriceNote>
                {yearly
                  ? `Billed as $${YEARLY_PRICE}/year — ${ANNUAL_DISCOUNT_PERCENT}% off the monthly price`
                  : "Billed monthly, cancel anytime"}
              </PriceNote>

              <CtaButton to="/waitlist">Start Your Free Trial</CtaButton>

              <FinePrint>
                Membership is required to join NudR. Includes a 7-day free trial
                — cancel anytime before it ends and you won't be charged.
                Subscriptions auto-renew after that until canceled. See the{" "}
                <Link to="/terms">Terms of Service</Link> for full billing,
                renewal, and refund terms.
              </FinePrint>
            </Card>
          </CardWrap>
        </Container>
      </Hero>

      <FeaturesSection>
        <Container>
          <SectionEyebrow>What's included</SectionEyebrow>
          <SectionTitle>Everything your membership includes</SectionTitle>
          <FeatureGrid>
            {FEATURES.map((f) => (
              <FeatureCard key={f.name}>
                <FeatureIcon>{f.icon}</FeatureIcon>
                <div>
                  <FeatureName>{f.name}</FeatureName>
                  <FeatureDesc>{f.desc}</FeatureDesc>
                </div>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Container>
      </FeaturesSection>

      <BackHome to="/">← Back to home</BackHome>
    </Page>
  );
}
